import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sparkles, Scissors, Check, HeartHandshake, Crown } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

const CHOICES = [
  { key: "makeup", title: "Makeup Artist", desc: "Schede trucco, prove, allergie, prodotti viso/occhi/labbra", icon: Sparkles },
  { key: "hair",   title: "Parrucchiere",  desc: "Schede capelli, acconciature, prove, accessori", icon: Scissors },
];

export default function Onboarding() {
  const { user, updateModules, logout } = useAuth();
  const navigate = useNavigate();
  const isPro = (user?.plan === "pro");
  const [primary, setPrimary] = useState(user?.primary_module || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!isPro && !primary) {
      toast.error("Seleziona la tua specializzazione per continuare");
      return;
    }
    setBusy(true);
    try {
      const modules = isPro
        ? ["wedding", "makeup", "hair"]
        : ["wedding", primary];
      await updateModules(modules, primary || undefined);
      toast.success("Configurazione salvata");
      navigate("/app/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="border-b border-border">
        <div className="max-w-5xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2.5">
            <BrandLogo size={32} />
            <span className="font-serif-heading text-xl">AI Bridal Manager™</span>
          </div>
          <button onClick={logout} className="text-sm text-neutral-600 hover:text-primary" data-testid="onboarding-logout">
            Esci
          </button>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-3xl w-full">
          <div className="text-center mb-12">
            <span className="label-uppercase">Configurazione iniziale</span>
            <h1 className="font-serif-heading text-4xl sm:text-5xl tracking-tight mt-4">
              Che tipo di professionista sei?
            </h1>
            <p className="mt-4 text-neutral-600 max-w-xl mx-auto">
              Wedding è sempre attivo. Scegli la tua specializzazione: nei piani <strong>FREE</strong> e <strong>BASIC</strong> è inclusa una sola
              modalità (Makeup oppure Hair). Con il piano <strong>PRO</strong> puoi attivare entrambe le modalità.
            </p>
          </div>

          {isPro && (
            <div className="mb-8 flex items-center justify-center gap-2 p-4 rounded-md bg-primary/5 border border-primary/20" data-testid="onboarding-pro-banner">
              <Crown className="w-4 h-4 text-primary" strokeWidth={1.5} />
              <span className="text-sm">Hai il piano PRO: <strong>entrambe le modalità verranno attivate</strong>. Puoi comunque scegliere la tua specializzazione principale.</span>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-5">
            {CHOICES.map((opt) => {
              const active = primary === opt.key;
              const Icon = opt.icon;
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setPrimary(opt.key)}
                  data-testid={`onboarding-primary-${opt.key}`}
                  className={`relative text-left p-8 border rounded-md transition-all ${
                    active ? "border-primary bg-primary/5 ring-1 ring-primary"
                           : "border-border bg-white hover:border-primary/40"
                  }`}
                >
                  {active && (
                    <div className="absolute top-4 right-4 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                      <Check className="w-3.5 h-3.5 text-primary-foreground" strokeWidth={2.5} />
                    </div>
                  )}
                  <Icon className="w-7 h-7 text-primary" strokeWidth={1.5} />
                  <h3 className="font-serif-heading text-2xl mt-5">{opt.title}</h3>
                  <p className="mt-2 text-sm text-neutral-600">{opt.desc}</p>
                </button>
              );
            })}
          </div>

          <div className="mt-12 flex flex-col items-center gap-3">
            <div className="flex items-center gap-2 text-xs text-neutral-500">
              <HeartHandshake className="w-3.5 h-3.5 text-primary" strokeWidth={1.5} />
              <span>Wedding è sempre incluso</span>
            </div>
            <Button
              size="lg"
              onClick={save}
              disabled={busy || (!isPro && !primary)}
              className="rounded-sm px-10"
              data-testid="onboarding-save-button"
            >
              {busy ? "Salvataggio…" : "Continua alla dashboard"}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
